/** Spec 069 P18: the one client error model. Kept out of `api.ts` so tests that mock `api.ts` still get it. */

/**
 * Every error body the backend has sent or will send (spec 069 P18): a string
 * `detail` (404/409), FastAPI's array `detail` (422), a dict `detail` or
 * top-level `message` (sandscape, plugin settings), and the P19 envelope
 * `{detail: string, code, fields?, retryable?}`. `apiFetch` parses all of them
 * once into an `ApiError`; callers branch on `code`, then `status`, never on
 * the message text.
 */
export type ErrorBody = unknown

export interface ApiFieldError {
  field: string
  item_key: string | null
  message: string
}

/** The two fixed sentences for failures that never reach a response (spec 069 D3). */
export const UNREACHABLE_MESSAGE = 'Cannot reach the server.'
export const TIMEOUT_MESSAGE = 'The request timed out.'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function validationEntryMessage(entry: unknown): string | null {
  if (!isRecord(entry) || typeof entry.msg !== 'string') return null
  const loc = Array.isArray(entry.loc) ? entry.loc : []
  return `${loc.join('.')}: ${entry.msg}`
}

/**
 * Renders a readable message from any error body, falling back to a generic
 * status-code message for anything else (e.g. a body that failed to parse).
 */
export function errorMessage(status: number, body: ErrorBody): string {
  if (isRecord(body)) {
    const detail = body.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      const parts = detail.map(validationEntryMessage).filter((part): part is string => part !== null)
      if (parts.length > 0) return parts.join('; ')
    }
    if (isRecord(detail)) {
      if (typeof detail.message === 'string') return detail.message
      if (typeof detail.detail === 'string') return detail.detail
    }
    if (typeof body.message === 'string') return body.message
  }
  return `Request failed (${status})`
}

/** The top-level (or dict-detail) machine `code` of an error body, or null. */
export function bodyCode(body: ErrorBody): string | null {
  if (!isRecord(body)) return null
  if (typeof body.code === 'string') return body.code
  if (isRecord(body.detail) && typeof body.detail.code === 'string') return body.detail.code
  return null
}

function bodyFields(body: ErrorBody): ApiFieldError[] {
  if (!isRecord(body)) return []
  if (Array.isArray(body.fields)) {
    const out: ApiFieldError[] = []
    for (const entry of body.fields) {
      if (!isRecord(entry) || typeof entry.field !== 'string' || typeof entry.message !== 'string') continue
      out.push({ field: entry.field, item_key: typeof entry.item_key === 'string' ? entry.item_key : null, message: entry.message })
    }
    return out
  }
  if (Array.isArray(body.detail)) {
    const out: ApiFieldError[] = []
    for (const entry of body.detail) {
      if (!isRecord(entry) || typeof entry.msg !== 'string') continue
      const loc = Array.isArray(entry.loc) ? entry.loc.map(String) : []
      out.push({ field: (loc[0] === 'body' ? loc.slice(1) : loc).join('.'), item_key: null, message: entry.msg })
    }
    return out
  }
  return []
}

export class ApiError extends Error {
  // Declared explicitly rather than as a constructor parameter property:
  // tsconfig sets erasableSyntaxOnly, which forbids TS-only runtime syntax.
  readonly status: number
  /** The parsed response body, so callers can branch on its shape if needed. */
  readonly body: ErrorBody
  /** Machine code: the server's `code`, or `unreachable` / `timeout` for status 0. Null when absent. */
  readonly code: string | null
  /** Per-field validation entries (envelope `fields`, or derived from a FastAPI 422 array). */
  readonly fields: ApiFieldError[]
  /** True only when the server marked the failure retryable. */
  readonly retryable: boolean

  constructor(status: number, body: ErrorBody, init?: { code?: string | null; message?: string }) {
    super(init?.message ?? errorMessage(status, body))
    this.name = 'ApiError'
    this.status = status
    this.body = body
    this.code = init?.code === undefined ? bodyCode(body) : init.code
    this.fields = bodyFields(body)
    this.retryable = isRecord(body) && body.retryable === true
  }
}

/** A caller-initiated abort (its own signal), as opposed to the timeout, which is `ApiError(0, 'timeout')`. */
export function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

/**
 * The one error-to-string conversion for the UI. An `ApiError` (or any
 * `Error`) yields its message; a caller abort or anything else yields
 * `fallback`.
 */
export function describeError(error: unknown, fallback = 'Something went wrong'): string {
  if (isAbort(error)) return fallback
  if (error instanceof Error && error.message !== '') return error.message
  return fallback
}
